/**
 * Inline messages for the static auth pages.
 *
 * These pages are EJS, not the Elm app, so `UI.errorMessage` and the toast
 * system are out of reach and every failure used to reach the user through
 * `alert()` -- which blocks the page, looks like a browser fault rather than
 * part of the product, and carried whatever string the upstream library
 * happened to produce ("Signups not allowed for otp").
 *
 * The wording rule matches the one `api/` handlers already follow (#175 M-1):
 * the detail goes to the console for whoever is debugging, and the user reads
 * something written for them.
 */

/** Styling kept in one place so the three pages cannot drift apart. */
const ERROR_CLASSES = [
  "text-sm",
  "rounded-md",
  "px-3",
  "py-2",
  "border-2",
  "text-center",
  "mt-5",
  "text-red-600",
  "border-red-600",
];

/**
 * Show `message` inside `container`, replacing any previous one.
 *
 * `detail` is logged rather than shown: an upstream error string is written for
 * whoever wrote the library, not for the person reading the page, and it can
 * disclose more than intended.
 */
export function showFormError(
  container: HTMLElement,
  message: string,
  detail?: unknown,
): void {
  if (detail !== undefined) console.error(message, detail);

  clearFormError(container);

  const paragraph = document.createElement("p");
  paragraph.dataset.formError = "true";
  paragraph.setAttribute("role", "alert");
  paragraph.classList.add(...ERROR_CLASSES);
  paragraph.textContent = message;

  container.append(paragraph);
}

export function clearFormError(container: HTMLElement): void {
  container.querySelector("[data-form-error]")?.remove();
}

/** The classes of a box notice: the error box, spaced below not above. */
export function noticeClasses(): string[] {
  return [...ERROR_CLASSES.filter((c) => c !== "mt-5"), "mb-5"];
}

/**
 * A page-level notice at the top of the card, above its headline (#515): why
 * the visitor landed on this page, not a problem with what they typed.
 */
export function showBoxNotice(box: HTMLElement, message: string): void {
  box.querySelector("[data-box-notice]")?.remove();
  const paragraph = document.createElement("p");
  paragraph.dataset.boxNotice = "true";
  paragraph.setAttribute("role", "alert");
  paragraph.classList.add(...noticeClasses());
  paragraph.textContent = message;
  box.prepend(paragraph);
}
